/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface TabBadges {
    projects: boolean;
    compliance: boolean;
    finance: boolean;
    impact: boolean;
    ngos: boolean; // For "Add NGO" tab (Company only)
}

export const useTabBadges = (userRole: string | null, companyId?: string, ngoId?: string) => {
    const [badges, setBadges] = useState<TabBadges>({
        projects: false,
        compliance: false,
        finance: false,
        impact: false,
        ngos: false
    });

    useEffect(() => {
        if (!userRole || (!companyId && !ngoId)) return;

        fetchBadges();

        // Set up real-time subscriptions
        const subscriptions = setupSubscriptions();

        return () => {
            subscriptions.then(subs => subs.forEach(sub => sub.unsubscribe()));
        };
    }, [userRole, companyId, ngoId]);

    const fetchBadges = async () => {
        if (userRole === 'client' && companyId) {
            await fetchCompanyBadges();
        } else if (userRole === 'ngo' && ngoId) {
            await fetchNGOBadges();
        }
    };

    const setupSubscriptions = async () => {
        const subs = [];

        if (userRole === 'client' && companyId) {
            // Company Subscriptions
            subs.push(supabase.channel('badge_fund_reqs').on('postgres_changes', { event: '*', schema: 'public', table: 'fund_requests' }, fetchCompanyBadges).subscribe());
            subs.push(supabase.channel('badge_kpi').on('postgres_changes', { event: '*', schema: 'public', table: 'kpi_reports' }, fetchCompanyBadges).subscribe());
            subs.push(supabase.channel('badge_docs').on('postgres_changes', { event: '*', schema: 'public', table: 'document_uploads' }, fetchCompanyBadges).subscribe());
            subs.push(supabase.channel('badge_ngos').on('postgres_changes', { event: '*', schema: 'public', table: 'ngos' }, fetchCompanyBadges).subscribe());
            subs.push(supabase.channel('badge_proj_ngos').on('postgres_changes', { event: '*', schema: 'public', table: 'project_ngos' }, fetchCompanyBadges).subscribe());
        } else if (userRole === 'ngo' && ngoId) {
            // NGO Subscriptions
            subs.push(supabase.channel('badge_fund_reqs').on('postgres_changes', { event: '*', schema: 'public', table: 'fund_requests' }, fetchNGOBadges).subscribe());
            subs.push(supabase.channel('badge_doc_reqs').on('postgres_changes', { event: '*', schema: 'public', table: 'document_requests' }, fetchNGOBadges).subscribe());
            subs.push(supabase.channel('badge_proj_ngos').on('postgres_changes', { event: '*', schema: 'public', table: 'project_ngos' }, fetchNGOBadges).subscribe());
            subs.push(supabase.channel('badge_kpi_ngo').on('postgres_changes', { event: '*', schema: 'public', table: 'kpi_reports' }, fetchNGOBadges).subscribe());
        }

        return subs;
    };

    const fetchCompanyBadges = async () => {
        if (!companyId) return;

        try {
            // 1. Finance: Pending Fund Requests
            // Need project IDs first
            const { data: projects } = await supabase.from('projects').select('id').eq('company_id', companyId);
            const projectIds = (projects as any)?.map((p: any) => p.id) || [];

            let financePending = false;
            if (projectIds.length > 0) {
                const { count } = await supabase
                    .from('fund_requests')
                    .select('*', { count: 'exact', head: true })
                    .in('project_id', projectIds)
                    .eq('status', 'pending');
                financePending = (count || 0) > 0;
            }

            // 2. Projects: Pending Reviews or KPI Reports
            let projectsPending = false;
            if (projectIds.length > 0) {
                // Pending Project Proposals
                const { count: proposalCount } = await supabase
                    .from('project_ngos')
                    .select('*', { count: 'exact', head: true })
                    .in('project_id', projectIds)
                    .eq('status', 'pending');

                // Pending KPI Reports
                const { count: kpiCount } = await supabase
                    .from('kpi_reports')
                    .select('*', { count: 'exact', head: true })
                    .in('project_id', projectIds)
                    .eq('status', 'submitted');

                projectsPending = (proposalCount || 0) > 0 || (kpiCount || 0) > 0;
            }

            // 3a. Impact: New submitted KPI reports from NGOs awaiting review
            let impactPending = false;
            if (projectIds.length > 0) {
                const { count: impactCount } = await supabase
                    .from('kpi_reports')
                    .select('*', { count: 'exact', head: true })
                    .in('project_id', projectIds)
                    .eq('status', 'submitted');
                impactPending = (impactCount || 0) > 0;
            }

            // 3. Compliance: Pending Document Approvals
            // We need to check document_uploads linked to NGOs associated with this company
            // Simplifying: Check uploads for active NGOs linked to this company
            const { data: linkedNgos } = await supabase.from('ngos').select('id').eq('company_id', companyId);
            const linkedNgoIds = (linkedNgos as any)?.map((n: any) => n.id) || [];

            let compliancePending = false;
            if (linkedNgoIds.length > 0) {
                const { count } = await supabase
                    .from('document_uploads')
                    .select('*', { count: 'exact', head: true })
                    .in('ngo_id', linkedNgoIds)
                    .eq('status', 'active'); 

                compliancePending = (count || 0) > 0;
            }

            // 4. NGOs (Add NGO): Pending Verification
            const { count: ngoCount } = await supabase
                .from('ngos')
                .select('*', { count: 'exact', head: true })
                .eq('company_id', companyId)
                .eq('status', 'submitted');

            const ngosPending = (ngoCount || 0) > 0;

            setBadges({
                projects: projectsPending,
                compliance: compliancePending,
                finance: financePending,
                impact: impactPending,
                ngos: ngosPending
            });

        } catch (error) {
            console.error('Error fetching company badges:', error);
        }
    };

    const fetchNGOBadges = async () => {
        if (!ngoId) return;

        try {
            // 1. Finance: Clarification Requested
            const { count: financeCount } = await supabase
                .from('fund_requests')
                .select('*', { count: 'exact', head: true })
                .eq('ngo_id', ngoId)
                .eq('status', 'clarification_requested');

            // 2. Projects: Pending Assignment OR Changes Requested
            const { count: projCount } = await supabase
                .from('project_ngos')
                .select('*', { count: 'exact', head: true })
                .eq('ngo_id', ngoId)
                .in('status', ['pending', 'changes_requested']);

            // 3. Compliance: Pending Document Requests
            const { count: docCount } = await supabase
                .from('document_requests')
                .select('*', { count: 'exact', head: true })
                .eq('requested_to_id', ngoId)
                .eq('status', 'pending');

            // 4. Impact: New approved KPI reports in last 14 days (new data for NGO to review)
            const twoWeeksAgo = new Date();
            twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);
            const { count: impactCount } = await supabase
                .from('kpi_reports')
                .select('*', { count: 'exact', head: true })
                .eq('ngo_id', ngoId)
                .eq('status', 'approved')
                .gte('updated_at', twoWeeksAgo.toISOString());

            setBadges({
                projects: (projCount || 0) > 0,
                compliance: (docCount || 0) > 0,
                finance: (financeCount || 0) > 0,
                impact: (impactCount || 0) > 0,
                ngos: false
            });

        } catch (error) {
            console.error('Error fetching NGO badges:', error);
        }
    };

    return badges;
};
